import { useState, useCallback, useMemo, useEffect } from 'react';
import { useStopwatch } from 'react-timer-hook';
import { IconButton } from '@mui/material';
import { PlayArrowIcon, StopIcon } from '../assets';
import { AutocompleteInput } from '../components';
import {
  useDispatch,
  startEmployeeTracking,
  stopEmployeeTracking,
  toggleTimer,
  getAssignedServicesBasicList,
} from '../state';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';

import { useIsMobile, useSetCameraConfig } from '../hooks';
import { arrayToObjByEnumOrId, employeeActivityTypes, paths } from '../lib';
import { useStyles } from './trackTime.styles';
import { useLocation } from 'react-router';
import { AssignedUserBasicResponse, SystemServiceType } from '../types';

interface RenderTimerProps {
  hours: number;
  minutes: number;
  seconds: number;
  isGlobalMobile?: boolean;
}

interface TimerButtonProps {
  isRunning: boolean;
  handleClick: () => void;
  disabled?: boolean;
  isGlobalMobile?: boolean;
}

export function TrackTimePage() {
  const { classes, css, cx } = useStyles();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const { pathname } = useLocation();

  const assignedServices = useSelector(
    (state: RootState) => state.system.assignedServices,
  );
  const assignedServicesMappings = useMemo(
    () => arrayToObjByEnumOrId(assignedServices),
    [assignedServices],
  );
  const [selectedAssignedService, setSelectedAssignedService] = useState('');
  const [
    selectedAssignedServiceEntityData,
    setSelectedAssignedServiceEntityData,
  ] = useState<Partial<AssignedUserBasicResponse> | null>(null);

  useSetCameraConfig({
    entityId: selectedAssignedServiceEntityData?.entityID as number,
    serviceType:
      selectedAssignedServiceEntityData?.serviceType as SystemServiceType,
    overrideTimerTaskEntity: true,
  });

  const showFullPage = pathname === `/${paths.TRACK_TIME}`;

  useEffect(() => {
    dispatch(getAssignedServicesBasicList(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { seconds, minutes, hours, isRunning, start, reset } = useStopwatch({
    autoStart: false,
  });

  const handleTimerClick = useCallback(async () => {
    const payload = {
      activityType: employeeActivityTypes.MANUAL_ACTIVITY,
      details: assignedServicesMappings[selectedAssignedService]?.name,
      fulfilledServiceAssigneeID: selectedAssignedService,
    };

    if (isRunning) {
      reset(undefined, false);
      const { error } = await dispatch(stopEmployeeTracking(payload));
      if (!error) {
        setSelectedAssignedService('');
        setSelectedAssignedServiceEntityData(null);
        dispatch(toggleTimer(false));
      }
    } else {
      start();
      const { error } = await dispatch(startEmployeeTracking(payload));
      if (!error) {
        dispatch(toggleTimer(true));
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedServicesMappings, isRunning, selectedAssignedService]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setSelectedAssignedService(value);
      const foundService = assignedServicesMappings[value];
      if (foundService) {
        setSelectedAssignedServiceEntityData({
          serviceType: foundService.serviceType,
          entityID: foundService.entityID,
        });
      }
    },
    [assignedServicesMappings],
  );

  if (isMobile && !showFullPage) {
    return (
      <div className={classes.globalMobileTimeContainer}>
        <TimeDisplay
          hours={hours}
          minutes={minutes}
          seconds={seconds}
          isGlobalMobile
        />
        <TimerButton
          isRunning={isRunning}
          handleClick={handleTimerClick}
          disabled={!selectedAssignedService}
          isGlobalMobile
        />
      </div>
    );
  }

  return (
    <div
      className={cx(classes.layout, { [classes.globalLayout]: !showFullPage })}
    >
      {showFullPage && <h1 className={classes.title}>Let’s track your time</h1>}
      <div className={classes.content}>
        <div
          className={cx(classes.bar, {
            [css({ padding: 0, paddingLeft: 8 })]: !showFullPage,
          })}
        >
          <AutocompleteInput
            value={selectedAssignedService}
            textFieldProps={{
              name: 'selectedAssignedService',
              placeholder: 'What are you working on?',
              onChange: handleChange,
              // value: selectedAssignedService,
              slotProps: {
                input: {
                  disableUnderline: !isMobile,
                  ...(!isMobile && { endAdornment: null }),
                },
              },
            }}
            autocompleteProps={{
              className: css({ flex: 1 }),
              options: assignedServices.map((service) => ({
                id: service.id,
                name: `${service.name} ${service.assignedByFullName ? ` - Assigned by ${service.assignedByFullName}` : ''}`,
              })),
              disabled: isRunning,
            }}
          />
          {!isMobile && (
            <div className={classes.timeContainer}>
              <TimeDisplay hours={hours} minutes={minutes} seconds={seconds} />
              <TimerButton
                isRunning={isRunning}
                handleClick={handleTimerClick}
                disabled={!selectedAssignedService}
              />
            </div>
          )}
        </div>
        {isMobile && showFullPage && (
          <div className={classes.mobileTimeContainer}>
            <div className={classes.mobileTime}>
              <TimeDisplay hours={hours} minutes={minutes} seconds={seconds} />
            </div>
            <TimerButton
              isRunning={isRunning}
              handleClick={handleTimerClick}
              disabled={!selectedAssignedService}
            />
          </div>
        )}
      </div>
    </div>
  );
}

const TimerButton = ({
  isRunning,
  handleClick,
  disabled,
  isGlobalMobile,
}: TimerButtonProps) => {
  const { classes, cx, css } = useStyles();
  return (
    <IconButton
      onClick={handleClick}
      className={cx(
        classes.timerBtn,
        {
          [classes.globalMobileTimerBtn]: isGlobalMobile,
        },
        isRunning ? classes.stopBtn : classes.startBtn,
      )}
      disabled={disabled}
    >
      {isRunning ? (
        <StopIcon
          className={cx(classes.timerIcon, {
            [css({ fontSize: 10 })]: isGlobalMobile,
          })}
        />
      ) : (
        <PlayArrowIcon className={classes.timerIcon} />
      )}
    </IconButton>
  );
};

function TimeDisplay({
  hours,
  minutes,
  seconds,
  isGlobalMobile,
}: RenderTimerProps) {
  const { classes, cx, css } = useStyles();
  return (
    <span
      className={cx(classes.time, { [css({ fontSize: 20 })]: isGlobalMobile })}
    >
      <span
        className={cx(classes.timeText, {
          [classes.timeTextGlobalMobil]: isGlobalMobile,
        })}
      >
        {formatTime(hours)}
      </span>
      <span
        className={cx(classes.timeText, {
          [classes.timeTickGlobalMobil]: isGlobalMobile,
        })}
      >
        :
      </span>
      <span
        className={cx(classes.timeText, {
          [classes.timeTextGlobalMobil]: isGlobalMobile,
        })}
      >
        {formatTime(minutes)}
      </span>
      <span
        className={cx(classes.timeText, {
          [classes.timeTickGlobalMobil]: isGlobalMobile,
        })}
      >
        :
      </span>
      <span
        className={cx(classes.timeText, {
          [classes.timeTextGlobalMobil]: isGlobalMobile,
        })}
      >
        {formatTime(seconds)}
      </span>
    </span>
  );
}

const formatTime = (time) => {
  return String(time).padStart(2, '0');
};
